/**
 * One ticket: the conversation, its files, its tasks, and the two note lists.
 *
 * The shape legacy's TicketView had — tabs on the left, the ticket's own facts
 * on the right — at a fifth of the size, because the parts it spent its length
 * on are gone: four tenant code paths per read, a websocket with its own
 * reconnect loop, a hard-coded roster of thirty support staff, and a chat that
 * had to be told by the browser what the browser had just saved.
 */

import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Pencil } from "lucide-react";
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { FilesTab } from "@/components/helpdesk/attachments";
import { TicketForm } from "@/components/helpdesk/ticket-form";
import { TicketInfo } from "@/components/helpdesk/ticket-info";
import { TicketNotes } from "@/components/helpdesk/ticket-notes";
import { TicketTasks } from "@/components/helpdesk/ticket-tasks";
import { TicketThread } from "@/components/helpdesk/ticket-thread";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { backLinkClass, BackArrow, Page, PageHeader } from "@/components/ui/page";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs } from "@/components/ui/tabs";
import { ROUTES } from "@/constants/routes";
import { errorMessage } from "@/services/api";
import {
  type Attachment,
  commentsQuery,
  isClosed,
  PRIORITY_LABEL,
  PRIORITY_TONE,
  STATUS_LABEL,
  STATUS_TONE,
  ticketQuery,
} from "@/services/helpdesk";
import { useCan } from "@/utils/permissions";
import { useTranslation } from "@/store/locale";

type TabKey = "comments" | "files" | "tasks" | "logs" | "resolution";

const TABS: readonly TabKey[] = ["comments", "files", "tasks", "logs", "resolution"];

const isTab = (value: string | null): value is TabKey =>
  value !== null && (TABS as readonly string[]).includes(value);

export default function HelpdeskTicketPage() {
  const { ticketId } = useParams() as { ticketId: string };
  return <TicketView id={Number(ticketId)} />;
}

function TicketView({ id }: { id: number }) {
  const t = useTranslation();
  // Which tab is open lives in the address bar, as it did in legacy: a link to
  // "the files on this ticket" is a real link, and a reload keeps the tab.
  const [params, setParams] = useSearchParams();
  const asked = params.get("tab");
  const ticket = useQuery(ticketQuery(id));
  // Read here as well as inside the thread so the Files tab can show what was
  // sent in the conversation beside what came with the ticket — react-query
  // hands both callers the one cached answer.
  const thread = useQuery(commentsQuery(id));
  const [editing, setEditing] = useState(false);
  const allow = useCan();

  if (ticket.isLoading) {
    return (
      <Page>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="rounded-card mt-4 h-[60dvh] w-full" />
      </Page>
    );
  }

  if (ticket.isError || !ticket.data) {
    return (
      <Page>
        <EmptyState
          kind="error"
          title={t("hd.notFoundTitle")}
          description={errorMessage(ticket.error, t("hd.notFoundBody"))}
          action={
            <Link to={ROUTES.HELPDESK} className={backLinkClass}>
              <BackArrow label={t("hd.backToList")} />
            </Link>
          }
        />
      </Page>
    );
  }

  const row = ticket.data;
  // A task has no tasks of its own, so it is not offered a tab for them.
  const tabs = TABS.filter((tab) => tab !== "tasks" || row.parent_id === null);
  const tab: TabKey = isTab(asked) && tabs.includes(asked) ? asked : "comments";

  // Everything attached anywhere on this ticket, in one list — which is what
  // the Files tab is for. Legacy's read the chat alone, so a file that came
  // with the ticket itself was not among its files.
  const files: Attachment[] = [
    ...row.attachments,
    ...(thread.data ?? []).flatMap((line) => line.attachments),
  ];

  return (
    <Page fill>
      <PageHeader
        // `backLinkClass` is a small round button that holds an arrow and
        // nothing else; the words belong in the breadcrumb below, where there
        // is room for them and where every other detail screen puts them.
        back={
          <Link to={ROUTES.HELPDESK} className={backLinkClass}>
            <BackArrow label={t("hd.backToList")} />
          </Link>
        }
        eyebrow={
          <span className="flex min-w-0 flex-wrap items-center gap-1.5">
            <Link to={ROUTES.HELPDESK} className="hover:text-accent">
              {t("nav.helpdesk")}
            </Link>
            <ChevronRight className="size-3 shrink-0" aria-hidden />
            {row.parent ? (
              <>
                <Link to={`/helpdesk/${row.parent.id}`} className="hover:text-accent truncate">
                  {row.parent.reference}
                </Link>
                <ChevronRight className="size-3 shrink-0" aria-hidden />
              </>
            ) : null}
            <span className="text-fg font-mono">{row.reference}</span>
          </span>
        }
        title={row.title}
        // Editing a closed ticket is refused by the API — it is a finished
        // record — so the button is not offered on one. Reopening it, from the
        // panel on the right, is the way back to editing.
        actions={
          allow("change__helpdesk_ticket") && !isClosed(row.status) ? (
            <Button variant="secondary" onClick={() => setEditing(true)}>
              <Pencil className="size-4" aria-hidden />
              {t("common.edit")}
            </Button>
          ) : undefined
        }
        description={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={STATUS_TONE[row.status]}>{t(STATUS_LABEL[row.status])}</Badge>
            <Badge tone={PRIORITY_TONE[row.priority]}>{t(PRIORITY_LABEL[row.priority])}</Badge>
            {row.archived_at ? <Badge tone="neutral">{t("hd.archivedBadge")}</Badge> : null}
          </span>
        }
      />

      {/* The tabs and the facts, side by side above `lg` and stacked below it,
          with the facts first on a narrow screen: on a phone the question
          "what is this ticket" comes before the conversation about it. */}
      <div className="grid min-h-0 flex-1 gap-5 lg:grid-cols-[1fr_22rem]">
        <div className="order-2 flex min-h-0 flex-col gap-4 lg:order-1">
          <Tabs
            label={t("hd.ticketSections")}
            value={tab}
            onChange={(next) =>
              setParams(
                (previous) => {
                  const search = new URLSearchParams(previous);
                  search.set("tab", next);
                  return search;
                },
                { replace: true },
              )
            }
            items={tabs.map((value) => ({
              value,
              label: t(
                value === "comments"
                  ? "hd.tabComments"
                  : value === "files"
                    ? "hd.tabFiles"
                    : value === "tasks"
                      ? "hd.tabTasks"
                      : value === "logs"
                        ? "hd.tabLogs"
                        : "hd.tabResolution",
              ),
              ...(value === "comments"
                ? { count: row.comment_count }
                : value === "files"
                  ? { count: files.length }
                  : value === "tasks"
                    ? { count: row.tasks.length }
                    : {}),
            }))}
          />

          <div className="flex min-h-0 flex-1 flex-col">
            {tab === "comments" ? (
              <TicketThread ticket={row} />
            ) : tab === "files" ? (
              <FilesTab files={files} />
            ) : tab === "tasks" ? (
              <TicketTasks ticket={row} />
            ) : tab === "logs" ? (
              <TicketNotes ticket={row} kind="activity_log" />
            ) : (
              <TicketNotes ticket={row} kind="resolution" />
            )}
          </div>
        </div>

        <div className="order-1 min-h-0 overflow-y-auto lg:order-2">
          <TicketInfo ticket={row} />
        </div>
      </div>

      {editing ? <TicketForm ticket={row} onClose={() => setEditing(false)} /> : null}
    </Page>
  );
}
